import { useState } from "react"
import { router } from "@inertiajs/react"
import { cn } from "@/lib/cn"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { AgentAvatar } from "./agent-avatar"
import { AgentStatusBadge } from "./agent-status-badge"
import type { Agent } from "@/types/agent"

type Props = {
  agents: Agent[]
}

// Agents list panel — search input header + dense table body. Clicking
// a row (or pressing Enter/Space on focus) navigates to that agent's
// edit page. The inline Edit button is the explicit affordance.
export function AgentTable({ agents }: Props) {
  const [query, setQuery] = useState("")

  const filtered = query.trim().length === 0
    ? agents
    : agents.filter((a) =>
        a.name.toLowerCase().includes(query.toLowerCase()) ||
        a.role.toLowerCase().includes(query.toLowerCase())
      )

  function open(id: number) {
    router.visit(`/agents/${id}/edit`)
  }

  return (
    <div className="overflow-hidden rounded-card border border-border bg-app-panel">
      <header className="flex items-center justify-between border-b border-border p-4">
        <div>
          <div className="font-semibold">All Agents</div>
          <div className="text-sm text-app-muted">
            Click an agent to open its edit page.
          </div>
        </div>
        <Input
          placeholder="Search agents…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </header>

      <div className="divide-y divide-border">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-app-muted">No agents match "{query}".</div>
        )}
        {filtered.map((a) => (
          // Row is a clickable region (not a <button>) so the inline Edit
          // <button> inside doesn't violate the "no nested interactive
          // elements" HTML rule. Keyboard support wired explicitly.
          <div
            key={a.id}
            role="button"
            tabIndex={0}
            onClick={() => open(a.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                open(a.id)
              }
            }}
            className={cn(
              "grid w-full cursor-pointer grid-cols-[1fr_120px_120px_90px_90px] items-center gap-4 p-4 text-left text-sm transition outline-none",
              "hover:bg-white/5 focus-visible:bg-white/5 focus-visible:ring-1 focus-visible:ring-brand-red/40",
            )}
          >
            <span className="flex items-center gap-3">
              <AgentAvatar name={a.name} />
              <span>
                <span className="block font-medium text-white">{a.name}</span>
                <span className="block text-app-muted">{a.role}</span>
              </span>
            </span>
            <span><AgentStatusBadge status={a.status} /></span>
            <span className="text-app-muted">{a.ticketsHandled.toLocaleString()} tickets</span>
            <span className="text-right text-vip-goldSoft">{a.qualityScore}%</span>
            <span className="text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  open(a.id)
                }}
              >
                Edit
              </Button>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
